import { AlertTriangle, CheckCircle, Download, ExternalLink, RefreshCw, X, XCircle } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { buildGoogleCalendarUrl, buildOutlookCalendarUrl } from '@/lib/calendarLinks';
import { cancelBooking, downloadIcs } from '@/services/bookingService';

import type { BookingConfirmation } from '@/types/scheduling.types';

interface BookingSuccessProps {
  confirmation: BookingConfirmation;
  timezone: string;
  token: string;
  onReschedule: () => void;
}

type ViewState = 'confirmed' | 'cancel-confirm' | 'cancelling' | 'cancelled';

function formatDate(utcString: string, timezone: string): string {
  return new Date(utcString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timezone,
  });
}

function formatTime(utcString: string, timezone: string): string {
  return new Date(utcString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });
}

function computeMinutes(startUtc: string, endUtc: string): number {
  return Math.round((new Date(endUtc).getTime() - new Date(startUtc).getTime()) / 60000);
}

export function BookingSuccess({ confirmation, timezone, token, onReschedule }: BookingSuccessProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [viewState, setViewState] = useState<ViewState>('confirmed');
  const [isDownloading, setIsDownloading] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const date = formatDate(confirmation.startAtUtc, timezone);
  const startTime = formatTime(confirmation.startAtUtc, timezone);
  const endTime = formatTime(confirmation.endAtUtc, timezone);
  const duration = computeMinutes(confirmation.startAtUtc, confirmation.endAtUtc);

  const CANCELLATION_CUTOFF_HOURS = 24;
  const hoursUntilInterview = (new Date(confirmation.startAtUtc).getTime() - Date.now()) / 3600000;
  const isPastCancelCutoff = hoursUntilInterview <= CANCELLATION_CUTOFF_HOURS;
  const isNearCancelCutoff = hoursUntilInterview <= CANCELLATION_CUTOFF_HOURS * 2 && !isPastCancelCutoff;

  const eventTitle = confirmation.interviewTitle ?? 'Interview — Kent Services';
  const eventDescription = `Interview with ${confirmation.interviewerName}`;

  const googleUrl = buildGoogleCalendarUrl({
    title: eventTitle,
    startUtc: confirmation.startAtUtc,
    endUtc: confirmation.endAtUtc,
    description: eventDescription,
  });

  const outlookUrl = buildOutlookCalendarUrl({
    title: eventTitle,
    startUtc: confirmation.startAtUtc,
    endUtc: confirmation.endAtUtc,
    description: eventDescription,
  });

  const handleDownloadIcs = useCallback(async () => {
    setIsDownloading(true);
    try {
      const blob = await downloadIcs(confirmation.bookingId, token);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'interview.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  }, [confirmation.bookingId, token]);

  const handleCancel = useCallback(async () => {
    setViewState('cancelling');
    try {
      await cancelBooking(confirmation.bookingId, token, cancelReason || undefined);
      setViewState('cancelled');
    } catch {
      setViewState('confirmed');
    }
  }, [confirmation.bookingId, token, cancelReason]);

  // ── Cancelled state ──────────────────────────────────────────────
  if (viewState === 'cancelled') {
    return (
      <div className='mx-auto max-w-md space-y-6 text-center'>
        <XCircle className='mx-auto h-16 w-16 text-red-400' />
        <h2 className='text-2xl font-bold text-gray-900'>{t('booking.success.cancelledTitle')}</h2>
        <p className='text-sm text-gray-500'>{t('booking.success.cancelledMessage')}</p>
        <button
          type='button'
          onClick={() => void navigate('/dashboard')}
          className='w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700'>
          {t('booking.success.backToDashboard')}
        </button>
      </div>
    );
  }

  // ── Confirmed state ──────────────────────────────────────────────
  return (
    <div className='mx-auto max-w-md space-y-6 text-center'>
      <CheckCircle className='mx-auto h-16 w-16 text-green-500' />

      <h2 className='text-2xl font-bold text-gray-900'>{t('booking.success.title')}</h2>

      {/* Booking details card */}
      <div className='rounded-lg bg-gray-50 p-6 text-left'>
        <div className='space-y-3'>
          <div className='flex justify-between text-sm'>
            <span className='text-gray-500'>{t('booking.success.date')}</span>
            <span className='font-medium text-gray-900'>{date}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-gray-500'>{t('booking.success.time')}</span>
            <span className='font-medium text-gray-900'>
              {startTime} – {endTime}
            </span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-gray-500'>{t('booking.success.duration')}</span>
            <span className='font-medium text-gray-900'>{t('booking.confirm.minutes', { count: duration })}</span>
          </div>
          {confirmation.interviewerName && (
            <div className='flex justify-between text-sm'>
              <span className='text-gray-500'>{t('booking.success.interviewer')}</span>
              <span className='font-medium text-gray-900'>{confirmation.interviewerName}</span>
            </div>
          )}
        </div>
      </div>

      <p className='text-sm text-gray-500'>{t('booking.success.message')}</p>

      {/* Add to Calendar buttons */}
      <div className='space-y-2'>
        <p className='text-xs font-medium uppercase tracking-wide text-gray-400'>
          {t('booking.success.addToCalendar')}
        </p>
        <div className='grid grid-cols-3 gap-2'>
          <a
            href={googleUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50'>
            <ExternalLink className='h-3.5 w-3.5' />
            {t('booking.success.googleCalendar')}
          </a>
          <a
            href={outlookUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50'>
            <ExternalLink className='h-3.5 w-3.5' />
            {t('booking.success.outlook')}
          </a>
          <button
            type='button'
            disabled={isDownloading}
            onClick={() => void handleDownloadIcs()}
            className='inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50'>
            <Download className='h-3.5 w-3.5' />
            {isDownloading ? t('booking.success.downloading') : t('booking.success.downloadIcs')}
          </button>
        </div>
      </div>

      {/* Cancel confirmation inline */}
      {viewState === 'cancel-confirm' && (
        <div className='rounded-lg border border-red-200 bg-red-50 p-4 text-left'>
          <p className='text-sm font-medium text-red-800'>{t('booking.success.cancelConfirmTitle')}</p>
          <p className='mt-1 text-xs text-red-600'>{t('booking.success.cancelConfirmMessage')}</p>

          {isNearCancelCutoff && (
            <div className='mt-2 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-2'>
              <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500' />
              <p className='text-xs text-amber-700'>
                {t('booking.cancel.cutoffWarning', { hours: CANCELLATION_CUTOFF_HOURS })}
              </p>
            </div>
          )}

          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder={t('booking.cancel.reasonPlaceholder')}
            rows={2}
            className='mt-3 w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400'
          />
          <p className='mt-1 text-xs text-gray-400'>{t('booking.cancel.reason')}</p>

          <div className='mt-3 flex gap-2'>
            <button
              type='button'
              onClick={() => void handleCancel()}
              className='rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700'>
              {t('booking.success.cancelConfirmButton')}
            </button>
            <button
              type='button'
              onClick={() => {
                setViewState('confirmed');
                setCancelReason('');
              }}
              className='rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50'>
              {t('booking.success.cancelKeep')}
            </button>
          </div>
        </div>
      )}

      {viewState === 'cancelling' && (
        <div className='flex items-center justify-center gap-2 py-2'>
          <div className='h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent' />
          <span className='text-sm text-gray-500'>{t('booking.success.cancelling')}</span>
        </div>
      )}

      {/* Action links */}
      {viewState === 'confirmed' && (
        <div className='flex items-center justify-center gap-6 pt-2'>
          <button
            type='button'
            onClick={onReschedule}
            className='inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700'>
            <RefreshCw className='h-3.5 w-3.5' />
            {t('booking.success.reschedule')}
          </button>
          {!isPastCancelCutoff ? (
            <button
              type='button'
              onClick={() => setViewState('cancel-confirm')}
              className='inline-flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-600'>
              <X className='h-3.5 w-3.5' />
              {t('booking.success.cancelInterview')}
            </button>
          ) : (
            <p className='text-xs text-gray-400'>
              {t('booking.cancel.pastCutoff', { hours: CANCELLATION_CUTOFF_HOURS })}
            </p>
          )}
        </div>
      )}

      {/* Done button */}
      <button
        type='button'
        onClick={() => void navigate('/dashboard')}
        className='w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700'>
        {t('booking.success.done')}
      </button>
    </div>
  );
}
